/**
 * React Query Hook: Create Metadata Item (new file from chat / Create button)
 *
 * Calls createMetadataItem GraphQL mutation and invalidates retrieval list on success.
 */

import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';
import { graphqlRequest } from '@/services/deployment/client';
import { CREATE_METADATA_ITEM_MUTATION } from '@/services/chat/queries';
import type {
  CreateMetadataItemInput,
  CreateMetadataItemResponse,
} from '@/services/chat/types';

async function createMetadataItem(
  input: CreateMetadataItemInput
): Promise<CreateMetadataItemResponse['createMetadataItem']> {
  const response = await graphqlRequest<CreateMetadataItemResponse>(
    CREATE_METADATA_ITEM_MUTATION,
    { input }
  );
  if (!response?.createMetadataItem) {
    throw new Error('Create metadata item failed. No response.');
  }
  return response.createMetadataItem;
}

export type CreateMetadataItemResult =
  CreateMetadataItemResponse['createMetadataItem'];

export interface UseCreateMetadataItemOptions {
  onSuccess?: (data: CreateMetadataItemResult) => void;
  onError?: (error: Error) => void;
}

/**
 * Hook to create a new metadata item via createMetadataItem API.
 * Invalidates retrievalMetadataItems so the sidebar list refetches.
 *
 * @returns React Query mutation (mutate/mutateAsync with CreateMetadataItemInput)
 */
export function useCreateMetadataItem(
  options?: UseCreateMetadataItemOptions
): UseMutationResult<
  CreateMetadataItemResult,
  Error,
  CreateMetadataItemInput
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createMetadataItem,
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['retrievalMetadataItems', variables.retrievalId],
      });
      options?.onSuccess?.(data);
    },
    onError: (error) => {
      options?.onError?.(error);
    },
  });
}
